import { lazy } from "react";
import {
	createBrowserRouter,
	createRoutesFromElements,
	Route,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import FallbackComponent from "@components/atoms/ErrorBoundary";
import { ProtectedRoute } from "@/services/auth/ProtectedRoute";
// import Layouts
import { DefaultLayout, AuthLayout } from "@/layouts";

// import Pages
import { ROUTES } from "@/consts";
import HomeRedirect from "./HomeRedirect";
import CreateVoucherPage from "../pages/Vouchers/CreateVoucher";
import EditVoucherPage from "../pages/Vouchers/EditVoucher";
import VouchersPage from "../pages/Vouchers";

const Issues = lazy(() => import("@/pages/Issues"));
const AnalyticsPage = lazy(() => import("@/pages/Analytics"));
const Taxonomies = lazy(() => import("@/pages/Taxonomies"));
const Media = lazy(() => import("@/pages/Media"));
const Banner = lazy(() => import("@/pages/Banner"));
const CreateBanner = lazy(() => import("@/pages/Banner/CreateBanner"));
const EditBanner = lazy(() => import("@/pages/Banner/EditBanner"));
const LandingPageBanner = lazy(() => import("@/pages/LandingPageBanner"));
const CreateLandingPageBanner = lazy(
	() => import("@/pages/LandingPageBanner/CreateBanner"),
);
const EditLandingPageBanner = lazy(
	() => import("@/pages/LandingPageBanner/EditBanner"),
);
const Assessments = lazy(() => import("@/pages/Assessments"));
const MediaDetails = lazy(() => import("@/pages/Media/MediaDetails"));
const EditMedia = lazy(() => import("@/pages/Media/EditMedia"));
const CourseDetailsPage = lazy(
	() => import("@/pages/Taxonomies/CourseDetails"),
);
const ReelsPage = lazy(() => import("@/pages/Reels"));
const ReelDetailsPage = lazy(() => import("@/pages/Reels/ReelDetails"));
const CreateReelPage = lazy(() => import("@/pages/Reels/CreateReel"));
const EditReelPage = lazy(() => import("@/pages/Reels/EditReel"));
const ProgramsPage = lazy(() => import("@/pages/Programs"));
const CreateProgram = lazy(() => import("@/pages/Programs/CreateProgram"));
const EditProgram = lazy(() => import("@/pages/Programs/EditProgram"));
const ProgramDetailsPage = lazy(
	() => import("@/pages/Programs/ProgramDetails"),
);
const ManualNotification = lazy(() => import("@/pages/Notifications/Manual"));
const CreateNotificationPage = lazy(
	() => import("@/pages/Notifications/Manual/CreateNotification"),
);
const EditNotificationPage = lazy(
	() => import("@/pages/Notifications/Manual/EditNotification"),
);
const AutomaticNotification = lazy(
	() => import("@/pages/Notifications/Automatic"),
);
const TagsPage = lazy(() => import("@/pages/Tags"));
const EditTagPage = lazy(() => import("@/pages/Tags/EditTag"));
const CreateTagPage = lazy(() => import("@/pages/Tags/CreateTag"));
const CoursesPage = lazy(() => import("@/pages/Courses"));
const CreateCoursePage = lazy(() => import("@/pages/Courses/CreateCourse"));
const EditCoursePage = lazy(() => import("@/pages/Courses/EditCourse"));
const PreviewCoursePage = lazy(() => import("@/pages/Courses/PreviewCourse"));
const ViewCoursePage = lazy(() => import("@/pages/Courses/ViewCourse"));
const SyncLogsPage = lazy(() => import("@/pages/Courses/SyncLogs"));
const SyncLogDetailsPage = lazy(() => import("@/pages/Courses/SyncLogDetails"));
const PermissionsPage = lazy(() => import("@/pages/Permissions"));
const CreateAdminPage = lazy(() => import("@/pages/Permissions/CreateAdmin"));
const RolesPage = lazy(() => import("@/pages/Roles"));
const CreateRolePage = lazy(() => import("@/pages/Roles/CreateRole"));
const EditRolePage = lazy(() => import("@/pages/Roles/EditRole"));
const FeedbacksListingPage = lazy(() => import("@/pages/FeedbacksListing"));
const FeedbackDetailsPage = lazy(
	() => import("@/pages/FeedbacksListing/FeedbackDetails"),
);
const MessageDetailsPage = lazy(
	() => import("@/pages/Messages/MessageDetails"),
);
const MessagesPage = lazy(() => import("@/pages/Messages"));
const UserFeedbackOverviewPage = lazy(
	() => import("@/pages/UserFeedbackOverview"),
);
const LiveSessionsPage = lazy(() => import("@/pages/LiveSessions"));
const CreateSessionPage = lazy(
	() => import("@/pages/LiveSessions/CreateSession"),
);
const EditSessionPage = lazy(() => import("@/pages/LiveSessions/EditSession"));
const ProvidersPage = lazy(() => import("@/pages/Providers"));
const CreateProviderPage = lazy(
	() => import("@/pages/Providers/CreateProvider"),
);
const EditProviderPage = lazy(() => import("@/pages/Providers/EditProvider"));
const PreviewProviderPage = lazy(
	() => import("@/pages/Providers/PreviewProvider"),
);
const CategoriesPage = lazy(() => import("@/pages/Categories"));
const PreviewCategoryPage = lazy(
	() => import("@/pages/Categories/PreviewCategory"),
);
const CreateCategoryPage = lazy(
	() => import("@/pages/Categories/CreateCategory"),
);
const EditCategoryPage = lazy(() => import("@/pages/Categories/EditCategory"));
const UsersPage = lazy(() => import("@/pages/Users"));
const CreateUserPage = lazy(() => import("@/pages/Users/CreateUser"));
const PreviewUserPage = lazy(() => import("@/pages/Users/PreviewUser"));
const EditUserPage = lazy(() => import("@/pages/Users/EditUser"));
const PartnersPage = lazy(() => import("@/pages/Partners"));
const PartnerDetailsPage = lazy(
	() => import("@/pages/Partners/PartnerDetails"),
);

const HubsPage = lazy(() => import("@/pages/Hubs"));

const NavbarSectionsPage = lazy(() => import("@/pages/NavbarSections"));

const CreateNavbarSectionPage = lazy(
	() => import("@/pages/NavbarSections/CreateNavbarSection"),
);
const EditNavbarSectionPage = lazy(
	() => import("@/pages/NavbarSections/EditNavbarSection"),
);
const PreviewNavbarSectionPage = lazy(
	() => import("@/pages/NavbarSections/PreviewNavbarSection"),
);
const ChangePasswordPage = lazy(() => import("@/pages/ChangePassword"));
const LoginPage = lazy(() => import("@/pages/UserAuth/Login"));

const TiersPage = lazy(() => import("@/pages/Tiers"));
const CreateTierPage = lazy(() => import("@/pages/Tiers/CreateTier"));
const EditTierPage = lazy(() => import("@/pages/Tiers/EditTier"));
const OpportunitiesPage = lazy(() => import("@/pages/Opportunities"));
const CreateOpportunityPage = lazy(
	() => import("@/pages/Opportunities/CreateOpportunity"),
);
const EditOpportunityPage = lazy(
	() => import("@/pages/Opportunities/EditOpportunity"),
);
const CompetitionsPage = lazy(() => import("@/pages/Competitions"));
const CreateCompetitionPage = lazy(
	() => import("@/pages/Competitions/CreateCompetition"),
);
const EditCompetitionPage = lazy(
	() => import("@/pages/Competitions/EditCompetition"),
);
const ViewCompetitionPage = lazy(
	() => import("@/pages/Competitions/ViewCompetition"),
);

const router = createBrowserRouter(
	createRoutesFromElements(
		<>
			<Route
				path="/"
				element={
					<ErrorBoundary FallbackComponent={FallbackComponent}>
						<DefaultLayout />
					</ErrorBoundary>
				}
			>
				<Route index element={<HomeRedirect />} />
				<Route element={<ProtectedRoute />}>
					<Route path={ROUTES.issues} element={<Issues />} />
					<Route
						path={ROUTES.ChangePassword}
						element={<ChangePasswordPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_DASHBOARD" />}>
					<Route path={ROUTES.Analytics} element={<AnalyticsPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_COURSES" />}>
					<Route path={ROUTES.Courses} element={<CoursesPage />} />
					<Route path={ROUTES.CreateCourse} element={<CreateCoursePage />} />
					<Route path={ROUTES.EditCourse} element={<EditCoursePage />} />
					<Route path={ROUTES.PreviewCourse} element={<PreviewCoursePage />} />
					<Route path={ROUTES.ViewCourse} element={<ViewCoursePage />} />
					<Route path={ROUTES.Assessments} element={<Assessments />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_SKILLSOFT" />}>
					<Route path={ROUTES.SyncLogs} element={<SyncLogsPage />} />
					<Route
						path={ROUTES.SyncLogDetails.path}
						element={<SyncLogDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_CATEGORIES" />}>
					<Route path={ROUTES.Categories} element={<CategoriesPage />} />
					<Route
						path={ROUTES.CreateCategory}
						element={<CreateCategoryPage />}
					/>
					<Route path={ROUTES.EditCategory} element={<EditCategoryPage />} />
					<Route
						path={ROUTES.PreviewCategory}
						element={<PreviewCategoryPage />}
					/>
					<Route
						path={ROUTES.NavbarSections}
						element={<NavbarSectionsPage />}
					/>
					<Route
						path={ROUTES.CreateNavbarSection}
						element={<CreateNavbarSectionPage />}
					/>
					<Route
						path={ROUTES.EditNavbarSection}
						element={<EditNavbarSectionPage />}
					/>
					<Route
						path={ROUTES.PreviewNavbarSection}
						element={<PreviewNavbarSectionPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_HUBS" />}>
					<Route path={ROUTES.Hubs} element={<HubsPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_PROGRAMS" />}>
					<Route path={ROUTES.Programs} element={<ProgramsPage />} />
					<Route path={ROUTES.CreateProgram} element={<CreateProgram />} />
					<Route path={ROUTES.EditProgram} element={<EditProgram />} />
					<Route
						path={ROUTES.ProgramDetails}
						element={<ProgramDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_REELS" />}>
					<Route path={ROUTES.Reels} element={<ReelsPage />} />
					<Route path={ROUTES.ReelDetails} element={<ReelDetailsPage />} />
					<Route path={ROUTES.CreateReel} element={<CreateReelPage />} />
					<Route path={ROUTES.EditReel} element={<EditReelPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_TAGS" />}>
					<Route path={ROUTES.Tags} element={<TagsPage />} />
					<Route path={ROUTES.EditTag} element={<EditTagPage />} />
					<Route path={ROUTES.CreateTag} element={<CreateTagPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_PROVIDERS" />}>
					<Route path={ROUTES.Providers} element={<ProvidersPage />} />
					<Route
						path={ROUTES.CreateProvider}
						element={<CreateProviderPage />}
					/>
					<Route path={ROUTES.EditProvider} element={<EditProviderPage />} />
					<Route
						path={ROUTES.PreviewProvider}
						element={<PreviewProviderPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_BANNER" />}>
					<Route path={ROUTES.Banner} element={<Banner />} />
					<Route path={ROUTES.CreateBanner} element={<CreateBanner />} />
					<Route path={ROUTES.EditBanner} element={<EditBanner />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_LANDING_PAGE_BANNER" />}>
					<Route
						path={ROUTES.LandingPageBanner}
						element={<LandingPageBanner />}
					/>
					<Route
						path={ROUTES.CreateLandingPageBanner}
						element={<CreateLandingPageBanner />}
					/>
					<Route
						path={ROUTES.EditLandingPageBanner}
						element={<EditLandingPageBanner />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_LIVE_SESSIONS" />}>
					<Route path={ROUTES.LiveSessions} element={<LiveSessionsPage />} />
					<Route path={ROUTES.CreateSession} element={<CreateSessionPage />} />
					<Route path={ROUTES.EditSession} element={<EditSessionPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_INCENTIVES" />}>
					<Route path={ROUTES.Tiers} element={<TiersPage />} />
					<Route path={ROUTES.CreateTier} element={<CreateTierPage />} />
					<Route path={ROUTES.EditTier} element={<EditTierPage />} />
					<Route path={ROUTES.Vouchers} element={<VouchersPage />} />
					<Route path={ROUTES.CreateVoucher} element={<CreateVoucherPage />} />
					<Route path={ROUTES.EditVoucher} element={<EditVoucherPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_OPPORTUNITIES" />}>
					<Route path={ROUTES.Opportunities} element={<OpportunitiesPage />} />
					<Route
						path={ROUTES.CreateOpportunity}
						element={<CreateOpportunityPage />}
					/>
					<Route
						path={ROUTES.EditOpportunity}
						element={<EditOpportunityPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_COMPETITIONS" />}>
					<Route path={ROUTES.Competitions} element={<CompetitionsPage />} />
					<Route
						path={ROUTES.CreateCompetition}
						element={<CreateCompetitionPage />}
					/>
					<Route
						path={ROUTES.EditCompetition}
						element={<EditCompetitionPage />}
					/>
					<Route
						path={ROUTES.ViewCompetition.path}
						element={<ViewCompetitionPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_TAXONOMIES" />}>
					<Route path={ROUTES.Taxonomies} element={<Taxonomies />} />
					<Route
						path={ROUTES.CourseDetails.path}
						element={<CourseDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_MEDIA" />}>
					<Route path={ROUTES.Media} element={<Media />} />
					<Route path={ROUTES.MediaDetails} element={<MediaDetails />} />
					<Route path={ROUTES.EditMedia} element={<EditMedia />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_MANUAL_NOTIFICATIONS" />}>
					<Route
						path={ROUTES.ManualNotification}
						element={<ManualNotification />}
					/>
					<Route
						path={ROUTES.CreateNotification}
						element={<CreateNotificationPage />}
					/>
					<Route
						path={ROUTES.EditNotification}
						element={<EditNotificationPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_AUTOMATIC_NOTIFICATIONS" />}>
					<Route
						path={ROUTES.AutomaticNotification}
						element={<AutomaticNotification />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_USERS" />}>
					<Route path={ROUTES.Users} element={<UsersPage />} />
					<Route path={ROUTES.CreateUser} element={<CreateUserPage />} />
					<Route path={ROUTES.PreviewUser} element={<PreviewUserPage />} />
					<Route path={ROUTES.EditUser.path} element={<EditUserPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_PARTNERS" />}>
					<Route path={ROUTES.Partners} element={<PartnersPage />} />
					<Route
						path={ROUTES.PartnerDetails.path}
						element={<PartnerDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_USER_FEEDBACK" />}>
					<Route
						path={ROUTES.UserFeedbackOverview}
						element={<UserFeedbackOverviewPage />}
					/>
					<Route
						path={ROUTES.FeedbacksListing}
						element={<FeedbacksListingPage />}
					/>
					<Route
						path={ROUTES.FeedbackDetails}
						element={<FeedbackDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_MESSAGES" />}>
					<Route path={ROUTES.Messages} element={<MessagesPage />} />
					<Route
						path={ROUTES.MessageDetails}
						element={<MessageDetailsPage />}
					/>
				</Route>
				<Route element={<ProtectedRoute tab="TAB_ADMINS" />}>
					<Route path={ROUTES.Permisstions} element={<PermissionsPage />} />
					<Route path={ROUTES.CreateAdmin} element={<CreateAdminPage />} />
				</Route>
				<Route element={<ProtectedRoute tab="TAB_ROLES" />}>
					<Route path={ROUTES.Roles} element={<RolesPage />} />
					<Route path={ROUTES.CreateRole} element={<CreateRolePage />} />
					<Route path={ROUTES.EditRole.path} element={<EditRolePage />} />
				</Route>
			</Route>
			<Route
				path="/"
				element={
					<ErrorBoundary FallbackComponent={FallbackComponent}>
						<AuthLayout />
					</ErrorBoundary>
				}
			>
				<Route path={ROUTES.Login} element={<LoginPage />} />
			</Route>
		</>,
	),
	{ basename: ROUTES.home },
);

export default router;
